"use client";

import { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";

type TooltipSide = "top" | "bottom" | "left" | "right";

type TooltipProps = {
  content: string;
  children: React.ReactNode;
  side?: TooltipSide;
  className?: string;
};

export function Tooltip({
  content,
  children,
  side = "bottom",
  className,
}: TooltipProps) {
  const tooltipId = useId();
  const triggerRef = useRef<HTMLSpanElement>(null);
  const [visible, setVisible] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    setMounted(true);
  }, []);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const gap = 8;

    switch (side) {
      case "top":
        setPosition({
          top: rect.top - gap,
          left: rect.left + rect.width / 2,
        });
        break;
      case "bottom":
        setPosition({
          top: rect.bottom + gap,
          left: rect.left + rect.width / 2,
        });
        break;
      case "left":
        setPosition({
          top: rect.top + rect.height / 2,
          left: rect.left - gap,
        });
        break;
      case "right":
        setPosition({
          top: rect.top + rect.height / 2,
          left: rect.right + gap,
        });
        break;
    }
  }, [side]);

  const show = useCallback(() => {
    updatePosition();
    setVisible(true);
  }, [updatePosition]);

  const hide = useCallback(() => {
    setVisible(false);
  }, []);

  useEffect(() => {
    if (!visible) return;

    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [visible, updatePosition]);

  const transform =
    side === "top"
      ? "translate(-50%, -100%)"
      : side === "bottom"
        ? "translate(-50%, 0)"
        : side === "left"
          ? "translate(-100%, -50%)"
          : "translate(0, -50%)";

  return (
    <>
      <span
        ref={triggerRef}
        className={cn("inline-flex", className)}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        aria-describedby={visible ? tooltipId : undefined}
      >
        {children}
      </span>
      {mounted && visible
        ? createPortal(
            <span
              id={tooltipId}
              role="tooltip"
              style={{
                position: "fixed",
                top: position.top,
                left: position.left,
                transform,
                zIndex: 9999,
              }}
              className="pointer-events-none max-w-[240px] whitespace-normal rounded-lg bg-[var(--admin-text,#111827)] px-2.5 py-1.5 text-center text-xs leading-snug text-[var(--admin-surface,#fff)] shadow-lg ring-1 ring-black/10"
            >
              {content}
            </span>,
            document.body
          )
        : null}
    </>
  );
}
